import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './stores/authStore'
import Layout from './components/layout/Layout'
import OnboardingPopup from './components/Onboarding/OnboardingPopup'
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import ChatPage from './pages/ChatPage'
import SettingsPage from './pages/SettingsPage'
import CodebasesPage from './pages/CodebasesPage'
import AdminPage from './pages/AdminPage'
import MultiAgentPage from './pages/MultiAgentPage'
import MCPServersPage from './pages/MCPServersPage'
import CodeEditorPage from './pages/CodeEditorPage'
import GitPage from './pages/GitPage'
import BillingPage from './pages/BillingPage'
import HelpPage from './pages/HelpPage'
import NotFoundPage from './pages/NotFoundPage'

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-dark-400">Loading...</p>
        </div>
      </div>
    )
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  
  return <>{children}</>
}

// Admin Route Component
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuthStore()
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-dark-400">Loading...</p>
        </div>
      </div>
    )
  }
  
  if (!isAuthenticated || user?.role !== 'superadmin') {
    return <Navigate to="/chat" replace />
  }
  
  return <>{children}</>
}

function App() {
  return (
    <>
      <OnboardingPopup />
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      
      {/* Protected Routes */}
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <Layout>
              <ChatPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/codebases"
        element={
          <ProtectedRoute>
            <Layout>
              <CodebasesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Multi-Agent Route */}
      <Route
        path="/multi-agent"
        element={
          <ProtectedRoute>
            <Layout>
              <MultiAgentPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* MCP Servers Route */}
      <Route
        path="/mcp-servers"
        element={
          <ProtectedRoute>
            <Layout>
              <MCPServersPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Code Editor Route */}
      <Route
        path="/code-editor"
        element={
          <ProtectedRoute>
            <Layout>
              <CodeEditorPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Git Operations Route */}
      <Route
        path="/git"
        element={
          <ProtectedRoute>
            <Layout>
              <GitPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Billing Route */}
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <Layout>
              <BillingPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Help Route */}
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <Layout>
              <HelpPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <Layout>
              <AdminPage />
            </Layout>
          </AdminRoute>
        }
      />
      
      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </>
  )
}

export default App
